"use client";

import { useEffect, useRef } from "react";

import { cn } from "@/lib/utils";

interface RevealProps {
  children: React.ReactNode;
  className?: string;
  /** Delay in ms before adding .visible once intersecting */
  delay?: number;
  /** Render as a different element — defaults to div */
  as?: keyof React.JSX.IntrinsicElements;
}

/**
 * Thin wrapper that adds the `.reveal` class and triggers `.visible`
 * via IntersectionObserver when the element enters the viewport.
 *
 * Pairs with the `.reveal` / `.reveal.visible` styles in globals.css.
 *
 * Usage:
 *   <Reveal as="h2" delay={100}>...</Reveal>
 *   <Reveal className="mt-4">...</Reveal>
 */
export function Reveal({
  children,
  className,
  delay = 0,
  as: Tag = "div",
}: RevealProps) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // If JS runs but IntersectionObserver isn't available, show immediately
    if (!("IntersectionObserver" in window)) {
      el.classList.add("visible");
      return;
    }

    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          timer = setTimeout(() => el.classList.add("visible"), delay);
          observer.unobserve(el);
        }
      },
      { threshold: 0.04, rootMargin: "0px 0px -20px 0px" },
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [delay]);

  return (
    // @ts-expect-error — polymorphic ref typing; safe here
    <Tag ref={ref} className={cn("reveal", className)}>
      {children}
    </Tag>
  );
}
